import type {
    SchoolProduct,
    SchoolProductCancellation,
    TabBarOption,
} from '@/features/school-product-tiles-v2/school-product-tiles-v2-type';

import React from 'react';

export default function useSchoolProduct(
    tabBar: TabBarOption[],
    products: SchoolProduct[],
    fallbackCancellation: SchoolProductCancellation | null,
) {
    const defaultTab = tabBar.find((tabBarOption) => tabBarOption.isDefault)?.name;
    const [isDetailsOverlayOpen, setIsDetailsOverlayOpen] = React.useState(() =>
        products.some((product) => product.detailsOverlay?.isOpen),
    );
    const [isReservationOverlayOpen, setIsReservationOverlayOpen] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState(() =>
        products.find((product) => product.detailsOverlay?.isOpen),
    );
    const [selectedTab, setSelectedTab] = React.useState<string>(defaultTab || '');
    const [effectiveCancellation, setEffectiveCancellation] = React.useState<SchoolProductCancellation | null>(null);

    const filteredProducts = React.useMemo(
        () =>
            (defaultTab && selectedTab.includes(defaultTab)) || tabBar.length === 0
                ? products
                : products.filter((product) => selectedTab.includes(product.tabBarKey)),
        [defaultTab, products, selectedTab, tabBar.length],
    );

    const toggleTab = (tabBarOption: TabBarOption) => {
        if (tabBarOption.isDefault && defaultTab) {
            setSelectedTab(defaultTab);
            return;
        }

        setSelectedTab(tabBarOption.name);
    };

    const toggleDetailsOverlay = (isOpen: boolean, product: SchoolProduct) => {
        setIsDetailsOverlayOpen(isOpen);
        setSelectedProduct(product);
        setEffectiveCancellation(mergeCancellation(product.cancellation));
    };

    const toggleReservationOverlay = (isOpen: boolean, product: SchoolProduct) => {
        setIsReservationOverlayOpen(isOpen);
        setIsDetailsOverlayOpen(false);
        setSelectedProduct(product);
    };

    const calculateKeyFactList = (product: SchoolProduct) => {
        return product.firstViewKeyFactCount
            ? product.keyfacts.slice(0, product.firstViewKeyFactCount)
            : product.keyfacts;
    };

    const mergeCancellation = (productCancellation?: SchoolProductCancellation | null) => {
        if (!productCancellation) {
            return fallbackCancellation;
        }

        return {
            condition: productCancellation.condition,
            overlay: productCancellation.overlay ?? fallbackCancellation?.overlay ?? null,
        };
    };

    return {
        calculateKeyFactList,
        effectiveCancellation,
        filteredProducts,
        isDetailsOverlayOpen,
        isReservationOverlayOpen,
        mergeCancellation,
        selectedProduct,
        selectedTab,
        setIsDetailsOverlayOpen,
        setIsReservationOverlayOpen,
        setSelectedProduct,
        toggleDetailsOverlay,
        toggleReservationOverlay,
        toggleTab,
    };
}
